import React, {FunctionComponent, useCallback, useContext, useState} from 'react';
import {InvoiceItemsTableItemRowProps} from "../InvoiceItemsTableItemRow/InvoiceItemsTableItemRow";
import {InvoiceContext} from "../../context/InvoiceContext";
import {Invoice} from "../../models/invoice.model";
import './InvoiceItemsTableCollapsibleItemRow.scss';
import {SmallButton} from "../ui-components/SmallButton/SmallButton";
import {InvoiceItemRowFile} from "../InvoiceItemsTableItemRow/InvoiceItemRowFile/InvoiceItemRowFile";
import {FilePreviewModal} from "../FilePreviewModal/FilePreviewModal";
import {File} from "../../models/file.model";
import {
    InvoiceItemRowFilePreview
} from "../InvoiceItemsTableItemRow/InvoiceItemRowFilePreview/InvoiceItemRowFilePreview";

export const InvoiceItemsTableCollapsibleItemRow: FunctionComponent<InvoiceItemsTableItemRowProps> = (props) => {
        const {item, itemSection} = props;
        const invoice: Invoice = useContext(InvoiceContext);
        const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
        const [currentFilePreviewIndex, setCurrentFilePreviewIndex] = useState<number | null>(null);

        const closeModal = useCallback(() => {
            setCurrentFilePreviewIndex(null);
        }, [setCurrentFilePreviewIndex]);

        const onViewImage = useCallback((image: File) => {
            setCurrentFilePreviewIndex(item.files.indexOf(image));
        }, [setCurrentFilePreviewIndex]);

        const toggleIsCollapsed = () => {
            setIsCollapsed(!isCollapsed);
        };

        const isContentShowing = () => {
            return !isCollapsed;
        }

        const getVisibleAdditionalField = () => {
            if (item.is_total_visible) {
                return item.total;
            }

            if (item.is_price_visible) {
                return item.unit_price;
            }

            if (item.is_quantity_visible) {
                return item.quantity;
            }

            if (item.is_taxed_visible) {
                return `${item.tax_name}: ${item.tax_value}`;
            }
        };

        return item && invoice ? (
            <div className='invoice-item-collapsible'>
                <header>
                    {
                        itemSection.is_item_title_visible
                            ? <div className='invoice-item-title'>
                                <span>{item.title}</span>
                            </div>
                            : <div className='invoice-item-title'/>
                    }

                    <div className='invoice-item-visible-additional-field'>
                        {getVisibleAdditionalField()}
                    </div>

                    <div className='show-more-button-container'>
                        <SmallButton onClick={() => {
                            toggleIsCollapsed()
                        }} className="show-more-button">
                            {isContentShowing() ? 'Hide Item' : 'Show More'}
                        </SmallButton>
                    </div>

                    {!isContentShowing() && item.files?.length && <div className="invoice-item-files">
                        {item.files.map((file, index) => {
                            return <InvoiceItemRowFilePreview key={index} file={file} onViewImage={onViewImage}/>
                        })}
                    </div>}
                </header>

                {
                    isContentShowing()
                        ? <main>
                            {
                                item.is_description_visible && item.description
                                    ? <div className="invoice-item-description">
                                        <p className="bold-text">Description: </p>
                                        <p>{item.description}</p>
                                    </div>
                                    : null
                            }

                            {
                                item.is_price_visible
                                    ? <div className='invoice-item-price'>
                                        <p className="bold-text">Unit Price:</p>
                                        <p>{item.unit_price}</p>
                                    </div>
                                    : null
                            }

                            {
                                item.is_unit_tax_amount_visible
                                    ? <div className='invoice-item-unit-tax'>
                                        <p className="bold-text">{itemSection.tax_short_name} Amount:</p>
                                        <p>{item.unit_tax_amount}</p>
                                    </div>
                                    : null
                            }

                            {
                                item.is_quantity_visible
                                    ? <div className='invoice-item-quantity'>
                                        <p className="bold-text">Quantity:</p>
                                        <p>{item.quantity}</p>
                                    </div>
                                    : null
                            }

                            {
                                item.is_taxed_visible
                                    ? <div className='invoice-item-tax'>
                                        <p className="bold-text">{item.tax_name}:</p>
                                        <p>{item.tax_value}</p>
                                    </div>
                                    : null
                            }

                            {
                                item.is_total_without_tax_visible
                                    ? <div className='invoice-item-amount-with-tax'>
                                        <p className="bold-text">Amount (Ex {itemSection.tax_short_name}):</p>
                                        <p>{item.total_without_tax}</p>
                                    </div>
                                    : null
                            }

                            {
                                item.is_total_visible
                                    ? <div className='invoice-item-total'>
                                        <p className="bold-text">Amount:</p>
                                        <p>{item.total}</p>
                                    </div>
                                    : null}

                            {
                                item.is_total_with_tax_visible
                                    ? <div className='invoice-item-amount-with-tax'>
                                        <p className="bold-text">Amount ({itemSection.tax_short_name} inc):</p>
                                        <p>{item.total_with_tax}</p>
                                    </div>
                                    : null
                            }

                            {item.files?.length && <div className="invoice-item-files">
                                {item.files.map((file, index) => {
                                    return <InvoiceItemRowFilePreview key={index} file={file} onViewImage={onViewImage}/>
                                })}
                            </div>}
                        </main>
                        : null
                }

                {currentFilePreviewIndex !== null && <FilePreviewModal files={item.files}
                                                                       currentFileIndex={currentFilePreviewIndex}
                                                                       onCloseModal={closeModal}/>}


            </div>
        ) : null;
    }
;
